import React, {
	useState,
	useRef,
	useEffect,
	useCallback,
	useMemo,
	useImperativeHandle,
} from 'react';
import {createPortal} from 'react-dom';
import {ChevronDown, X} from 'lucide-react';
import {Controller} from 'react-hook-form';
import {twMerge} from 'tailwind-merge';
import {useIntl} from 'contexts/IntlContext';
import Input from './form/Input';
import {usePopper} from 'react-popper';
import MenuItem from './Menu/MenuItem';
import Button from './Button';
import {selectSizes} from 'utils/uiSizes';
import {activeVariantClasses, modifiedVariantClasses} from 'utils/formVariants';

const defaultGetOptionValue = option => option.value || option.id;
const defaultGetOptionLabel = option => {
	const label = option.label || option.title || option.name;
	// Ensure we never return an object - convert to string if needed
	if (label && typeof label === 'object' && !React.isValidElement(label)) {
		return String(label);
	}
	return label || '';
};

const defaultGetSelectionText =
	(getOptionValue = defaultGetOptionValue, getOptionLabel = defaultGetOptionLabel) =>
	(value, options) => {
		// Helper to ensure label is renderable
		const ensureRenderableLabel = label => {
			if (label === null || label === undefined) return '';
			if (typeof label === 'string' || typeof label === 'number') return label;
			if (React.isValidElement(label)) return label;
			// If label is an object or other non-renderable value, convert to string
			// This prevents "Objects are not valid as a React child" errors
			return String(label);
		};

		// Handle single value (non-array)
		if (value !== null && value !== undefined && !Array.isArray(value)) {
			const option = options.find(opt => getOptionValue(opt) === value);
			if (!option) return '';
			const label = getOptionLabel(option);
			// Ensure label is renderable
			return ensureRenderableLabel(label);
		}
		// Handle array of values
		const labels = options
			.filter(opt => value && value.includes(getOptionValue(opt)))
			.map(getOptionLabel)
			.map(ensureRenderableLabel);
		// If any label is JSX/React element, return array (React will render each element)
		// Otherwise join as string
		const hasJSX = labels.some(label => React.isValidElement(label));
		return hasJSX ? labels : labels.join(', ');
	};

const MultiSelect = React.forwardRef(
	(
		{
			required,
			error,
			name,
			id,
			control,
			options = [],
			getOptionLabel = defaultGetOptionLabel,
			getOptionValue = defaultGetOptionValue,
			disabled,
			placeholder,
			className,
			wrapperClassName,
			menuClassName,
			menuContentClassName,
			resetButtonClassName,
			buttonClassName,
			size = 'md',
			variant = 'default',
			modified = false,
			getSelectionText = defaultGetSelectionText(getOptionValue, getOptionLabel),
			renderSelection, // Custom render function for visual content (dots, badges, etc.)
			onChange,
			value = [],
			rules,
			showCountAt = 2,
			menuPlacement = 'bottom-start',
			sameWidth = false,
			useSearch = true,
			isValid,
			isLoading = false,
			onSearchChange,
			hoistSelected = true,
			// New props for single selection mode
			single = false,
			clearable = true,
			showSearchAtCount = 10,
			buttonProps,
			customTrigger,
		},
		ref,
	) => {
		const intl = useIntl();
		const [open, setOpen] = useState(false);
		const dropdownRef = useRef(null);
		const searchInputRef = useRef(null);
		const [search, setSearch] = useState('');
		const [highlightedIndex, setHighlightedIndex] = useState(0);
		const [isClosing, setIsClosing] = useState(false);

		const [referenceElement, setReferenceElement] = useState(null);
		const [popperElement, setPopperElement] = useState(null);
		const referenceRef = useCallback(node => setReferenceElement(node), []);
		const popperRef = useCallback(node => setPopperElement(node), []);

		const optionRefs = useRef([]);
		const menuRef = useRef(null);
		function getOptionRef(idx) {
			if (!optionRefs.current[idx]) {
				optionRefs.current[idx] = React.createRef();
			}
			return optionRefs.current[idx];
		}

		// Store a reference to the button for form validation
		const buttonRef = useRef(null);

		const normalizeValue = useCallback(
			val => {
				if (single) {
					// For single selection, return the value as-is or null/undefined
					// If value is an object with an id property, extract the id
					if (val && typeof val === 'object' && !Array.isArray(val) && 'id' in val) {
						return val.id;
					}
					return val === '' || val === null || val === undefined ? null : val;
				}

				// For multiple selection, maintain existing logic
				if (Array.isArray(val)) {
					// If array contains objects with id property, extract ids
					return val.map(v =>
						v && typeof v === 'object' && !Array.isArray(v) && 'id' in v ? v.id : v,
					);
				}
				if (typeof val === 'string') {
					return val
						.split(',')
						.map(v => v.trim())
						.filter(Boolean);
				}
				return [];
			},
			[single],
		);

		// Memoize filtered options to prevent unnecessary re-renders
		const filteredOptions = useMemo(() => {
			// Apply search filtering first
			const filtered = search
				? options.filter(option => {
						const label = getOptionLabel(option);
						// Only filter by string labels (skip JSX labels in search)
						if (typeof label === 'string') {
							return label.toLowerCase().includes(search.toLowerCase());
						}
						// For non-string labels, convert to string for search
						const labelStr = String(label || '');
						return labelStr.toLowerCase().includes(search.toLowerCase());
				  })
				: options;

			// Return filtered options without hoisting when hoistSelected is false
			return filtered;
		}, [search, options, getOptionLabel]);

		// Reset highlighted index when filtered options change significantly
		useEffect(() => {
			if (open && filteredOptions.length > 0) {
				setHighlightedIndex(0);
			}
		}, [filteredOptions.length, open]);

		// Handle search changes and notify parent component
		useEffect(() => {
			if (onSearchChange && search !== '') {
				onSearchChange(search);
			}
		}, [search, onSearchChange]);

		// Generate fallback placements that preserve alignment (start/end)
		const getFallbackPlacements = useMemo(() => {
			const placement = menuPlacement || 'bottom-start';
			const parts = placement.split('-');
			const side = parts[0]; // 'top', 'bottom', 'left', 'right'
			const alignment = parts[1]; // 'start', 'end', or undefined

			// Map opposite sides
			const oppositeSide =
				{
					top: 'bottom',
					bottom: 'top',
					left: 'right',
					right: 'left',
				}[side] || 'top';

			// Build fallback placements preserving alignment
			const fallbacks = [];
			if (alignment) {
				// Preserve alignment: bottom-start -> top-start, bottom-end -> top-end
				fallbacks.push(`${oppositeSide}-${alignment}`);
			} else {
				// No alignment: bottom -> top
				fallbacks.push(oppositeSide);
			}

			// Add horizontal alternatives if vertical placement
			if (side === 'top' || side === 'bottom') {
				if (alignment === 'start') {
					fallbacks.push('right-start', 'left-start');
				} else if (alignment === 'end') {
					fallbacks.push('right-end', 'left-end');
				} else {
					fallbacks.push('right', 'left');
				}
			}

			// Add vertical alternatives if horizontal placement
			if (side === 'left' || side === 'right') {
				if (alignment === 'start') {
					fallbacks.push('top-start', 'bottom-start');
				} else if (alignment === 'end') {
					fallbacks.push('top-end', 'bottom-end');
				} else {
					fallbacks.push('top', 'bottom');
				}
			}

			return fallbacks;
		}, [menuPlacement]);

		const {styles, attributes, update} = usePopper(referenceElement, popperElement, {
			placement: menuPlacement,
			modifiers: [
				{
					name: 'flip',
					options: {
						fallbackPlacements: getFallbackPlacements,
					},
				},
				{
					name: 'preventOverflow',
					options: {padding: 8, boundary: 'viewport'},
				},
				{
					name: 'offset',
					options: {offset: [0, 5]},
				},
			],
		});

		// Set menu width to match button when open, if sameWidth is true
		useEffect(() => {
			if (sameWidth && open && popperElement && referenceElement) {
				popperElement.style.width = `${referenceElement.offsetWidth}px`;
			} else if (popperElement && !sameWidth) {
				popperElement.style.width = '';
			}
		}, [sameWidth, open, popperElement, referenceElement]);

		// Handle opening the dropdown - focus search input or menu
		useEffect(() => {
			if (open && !isClosing) {
				// Determine if search input will be shown
				const showSearchInput = useSearch && options.length >= showSearchAtCount;

				if (showSearchInput && searchInputRef.current) {
					searchInputRef.current.focus();
				} else if (menuRef.current) {
					// Focus the menu itself when search is not shown
					menuRef.current.focus();
				}

				if (typeof update === 'function') {
					setTimeout(() => update(), 0);
				}
			}
		}, [open, isClosing, update, useSearch, options.length, showSearchAtCount]);

		// Handle scrolling to highlighted option
		useEffect(() => {
			if (
				open &&
				!isClosing &&
				optionRefs.current &&
				optionRefs.current.length > 0 &&
				optionRefs.current[highlightedIndex] &&
				optionRefs.current[highlightedIndex].current
			) {
				optionRefs.current[highlightedIndex].current.scrollIntoView({block: 'nearest'});
			}
		}, [highlightedIndex, open, isClosing]);

		const handleSelect = useCallback(
			(optionValue, field) => {
				// Normalize field.value first to handle objects with id property
				const normalizedFieldValue = normalizeValue(field.value);
				let newValue;

				if (single) {
					// For single selection, just set the value or clear if same value selected
					if (normalizedFieldValue === optionValue) {
						newValue = clearable ? null : optionValue;
					} else {
						newValue = optionValue;
					}
				} else {
					// For multiple selection, maintain existing logic
					if (normalizedFieldValue?.includes(optionValue)) {
						newValue = normalizedFieldValue.filter(v => v !== optionValue);
					} else {
						newValue = [...(normalizedFieldValue || []), optionValue];
					}
				}

				field.onChange(newValue);
				// Uncontrolled, field.onChange is the onChange prop itself: call it only once.
				if (onChange !== field.onChange) onChange?.(newValue);
			},
			[onChange, single, clearable, normalizeValue],
		);

		const handleReset = useCallback(
			(field, e) => {
				e.stopPropagation();
				const resetValue = single ? null : [];
				field.onChange(resetValue);
				if (onChange !== field.onChange) onChange?.(resetValue);
			},
			[onChange, single],
		);

		const handleKeyDown = useCallback(
			(e, field, optionsList) => {
				if (!open || isClosing) return;
				if (e.key === 'ArrowDown') {
					e.preventDefault();
					let nextIndex = (highlightedIndex + 1) % optionsList.length;
					// Skip disabled options
					while (optionsList[nextIndex]?.disabled && nextIndex !== highlightedIndex) {
						nextIndex = (nextIndex + 1) % optionsList.length;
					}
					setHighlightedIndex(nextIndex);
				} else if (e.key === 'ArrowUp') {
					e.preventDefault();
					let prevIndex =
						(highlightedIndex - 1 + optionsList.length) % optionsList.length;
					// Skip disabled options
					while (optionsList[prevIndex]?.disabled && prevIndex !== highlightedIndex) {
						prevIndex = (prevIndex - 1 + optionsList.length) % optionsList.length;
					}
					setHighlightedIndex(prevIndex);
				} else if (e.key === 'Enter') {
					e.preventDefault();
					const option = optionsList[highlightedIndex];
					if (option && !option.disabled) {
						handleSelect(getOptionValue(option), field);
						// Close dropdown after selection in single mode
						if (single) {
							setIsClosing(true);
							setOpen(false);
						}
					}
				} else if (e.key === 'Escape') {
					e.preventDefault();
					setIsClosing(true);
					setOpen(false);
				} else if (e.key === 'Tab') {
					e.preventDefault();
					setIsClosing(true);
					setOpen(false);
					// Find the next focusable element after closing
					setTimeout(() => {
						const focusableElements = document.querySelectorAll(
							'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
						);
						const currentIndex = Array.from(focusableElements).indexOf(referenceElement);

						if (currentIndex !== -1) {
							const nextIndex = e.shiftKey ? currentIndex - 1 : currentIndex + 1;
							const nextElement = focusableElements[nextIndex];
							if (nextElement) {
								nextElement.focus();
							}
						}
					}, 0);
				}
			},
			[open, isClosing, highlightedIndex, handleSelect, getOptionValue, single],
		);

		// Handle click outside
		useEffect(() => {
			if (!open) return;
			function handleClickOutside(event) {
				if (
					dropdownRef.current &&
					!dropdownRef.current.contains(event.target) &&
					popperElement &&
					!popperElement.contains(event.target)
				) {
					setIsClosing(true);
					setOpen(false);
				}
			}
			document.addEventListener('mousedown', handleClickOutside);
			return () => {
				document.removeEventListener('mousedown', handleClickOutside);
			};
		}, [open, popperElement]);

		// Reset closing state when dropdown closes
		useEffect(() => {
			if (!open && isClosing) {
				setIsClosing(false);
			}
		}, [open, isClosing]);

		// Warn if both control and onChange/value are used, or if neither is used
		React.useEffect(() => {
			if (control && (onChange || value !== undefined)) {
				if (process.env.NODE_ENV !== 'production') {
					console.warn(
						'[MultiSelect] Do not use both `control` and `onChange`/`value` (from register) at the same time. Use either `control` (with `name` and `rules`) or `register` pattern, not both.',
					);
				}
			}
			if (!control && !(onChange || value !== undefined)) {
				if (process.env.NODE_ENV !== 'production') {
					console.warn(
						'[MultiSelect] You must use either `control` (with `name` and `rules`) or `register` (with `onChange`/`value`).',
					);
				}
			}
		}, [control, onChange, value]);

		function renderDropdown(field) {
			const normalizedValue = normalizeValue(field.value);

			// Apply hoistSelected sorting if enabled
			const finalOptions = hoistSelected
				? filteredOptions.sort((a, b) => {
						const aValue = getOptionValue(a);
						const bValue = getOptionValue(b);

						const aSelected = single
							? normalizedValue === aValue
							: normalizedValue?.includes(aValue);
						const bSelected = single
							? normalizedValue === bValue
							: normalizedValue?.includes(bValue);

						if (aSelected && !bSelected) return -1;
						if (!aSelected && bSelected) return 1;
						return 0;
				  })
				: filteredOptions;

			const selectionLength = single
				? normalizedValue !== null &&
				  normalizedValue !== undefined &&
				  normalizedValue !== ''
					? 1
					: 0
				: Array.isArray(normalizedValue) && normalizedValue.length > 0
				? normalizedValue.length
				: 0;

			const menu = (
				<div
					ref={node => {
						popperRef(node);
						menuRef.current = node;
					}}
					style={styles.popper}
					{...attributes.popper}
					className={twMerge(
						'z-[500] mt-1 max-w-60 min-w-[240px] bg-white dark:bg-gray-900 border overflow-hidden border-gray-200 dark:border-gray-700 rounded shadow-lg',
						'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500 focus-visible:ring-opacity-20 focus-visible:border-opacity-50',
						menuClassName,
					)}
					data-multiselect-menu="true"
					onKeyDown={e => handleKeyDown(e, field, finalOptions)}
					tabIndex={-1}
					role="listbox"
				>
					{/* Search input */}
					{useSearch && options.length >= showSearchAtCount && (
						<div className="bg-white dark:bg-gray-900 p-2 border-b border-gray-100 dark:border-gray-700">
							<div className="relative">
								<Input
									type="text"
									size={size}
									className="w-full pr-8"
									placeholder={intl.formatMessage({id: 'Search...'})}
									value={search}
									onChange={e => {
										setSearch(e.target.value);
										setHighlightedIndex(0);
									}}
									onClick={e => e.stopPropagation()}
									ref={searchInputRef}
								/>
								{search.length > 0 && (
									<button
										type="button"
										className="absolute right-2 top-1/2 -translate-y-1/2 size-5 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
										onClick={e => {
											e.stopPropagation();
											setSearch('');
											setHighlightedIndex(0);
											// Refocus the input after clearing
											setTimeout(() => {
												if (searchInputRef.current) {
													searchInputRef.current.focus();
												}
											}, 0);
										}}
										aria-label={intl.formatMessage({id: 'Clear search'})}
									>
										<X
											className="size-3 text-gray-400 dark:text-gray-500"
											strokeWidth={2.5}
										/>
									</button>
								)}
							</div>
						</div>
					)}
					<div
						className={twMerge(
							'[z-100] bg-white dark:bg-gray-900 p-2 border-b border-gray-100 dark:border-gray-700 overflow-auto max-h-60 space-y-0.5',
							menuContentClassName,
						)}
					>
						{isLoading && (
							<div className="p-2 text-gray-400 dark:text-gray-500">
								{intl.formatMessage({id: 'Loading...'})}
							</div>
						)}
						{!isLoading && finalOptions.length === 0 && (
							<div className="p-2 text-gray-400 dark:text-gray-500">
								{intl.formatMessage({id: 'No options'})}
							</div>
						)}
						{!isLoading &&
							finalOptions.map((option, idx) => {
								const optionValue = getOptionValue(option);
								const selected = single
									? normalizedValue === optionValue
									: normalizedValue?.includes(optionValue);
								const highlighted = idx === highlightedIndex;
								const isDisabled = option.disabled === true;

								return (
									<MenuItem
										key={optionValue}
										ref={getOptionRef(idx)}
										option={option}
										index={idx}
										getOptionLabel={getOptionLabel}
										getOptionValue={getOptionValue}
										highlighted={highlighted}
										selected={selected}
										disabled={isDisabled}
										showCheck={true}
										size={size}
										variant="pill"
										onClick={(option, optionValue) => {
											handleSelect(optionValue, field);
											// Close dropdown after selection in single mode
											if (single) {
												setIsClosing(true);
												setOpen(false);
											}
										}}
									/>
								);
							})}
					</div>
				</div>
			);

			const sizeClass = selectSizes[size] || selectSizes.md;

			// Combined ref callback for popper and Controller
			const buttonRefCallback = node => {
				// Set reference element for popper positioning
				referenceRef(node);
				// Store button reference
				buttonRef.current = node;
				// Call field.ref if it exists (for Controller form validation)
				if (field.ref && typeof field.ref === 'function') {
					field.ref(node);
				}
			};

			// If customTrigger is provided, use it instead of the default button
			const triggerElement = customTrigger ? (
				React.cloneElement(customTrigger, {
					onClick: e => {
						// Call original onClick if exists
						if (customTrigger.props.onClick) {
							customTrigger.props.onClick(e);
						}
						// Prevent label clicks from opening dropdown (label should only focus)
						if (e.detail === 0) {
							// Programmatic click (likely from label), just ensure it's focused
							if (buttonRef.current) {
								buttonRef.current.focus();
							}
							return;
						}
						// Regular click, toggle dropdown
						if (!isClosing) {
							setOpen(o => !o);
						}
					},
					ref: buttonRefCallback,
					'aria-haspopup': 'listbox',
					'aria-expanded': open,
					disabled: disabled || customTrigger.props.disabled,
				})
			) : (
				<Button
					type="button"
					id={field.id || name}
					variant="outline"
					disabled={disabled}
					size={size}
					className={twMerge(
						'flex items-center gap-1 w-full h-full rounded-md text-left leading-6',
						// Text size and uniform horizontal padding per size
						size === 'xs' ? 'text-xs px-2' : size === 'sm' ? 'text-sm px-2.5' : 'px-3',
						// Base styles
						'border shadow-sm bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100',
						// Active variant
						variant === 'active' && activeVariantClasses,
						// Modified variant
						modified && modifiedVariantClasses,
						// Focus styles for keyboard navigation
						'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500 focus-visible:ring-opacity-20 focus-visible:border-opacity-50',
						// Focus styles for programmatic focus (label click, form validation)
						'focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:ring-opacity-20 focus:border-opacity-50',
						error &&
							'ring-red-300 dark:ring-red-700 focus:ring-red-500 focus-visible:ring-red-500',
						'disabled:text-gray-500 dark:disabled:text-gray-400 disabled:opacity-100 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed disabled:hover:bg-gray-100 dark:disabled:hover:bg-gray-800',
						className,
					)}
					title={
						single &&
						normalizedValue !== null &&
						normalizedValue !== undefined &&
						normalizedValue !== ''
							? (() => {
									const option = options.find(
										opt => getOptionValue(opt) === normalizedValue,
									);
									if (!option) return '';
									const label = getOptionLabel(option);
									// Ensure label is renderable (string, number, or convert to string)
									if (label === null || label === undefined) return '';
									if (typeof label === 'string' || typeof label === 'number')
										return String(label);
									if (React.isValidElement(label)) return label;
									// If label is an object or other non-renderable value, convert to string
									return String(label);
							  })()
							: getSelectionText(normalizedValue, options)
					}
					onClick={e => {
						// Prevent label clicks from opening dropdown (label should only focus)
						// Check if this was triggered by a label click
						if (e.detail === 0) {
							// Programmatic click (likely from label), just ensure it's focused
							e.currentTarget.focus();
							return;
						}
						// Regular click, toggle dropdown
						if (!isClosing) {
							setOpen(o => !o);
						}
					}}
					ref={buttonRefCallback}
					aria-haspopup="listbox"
					aria-expanded={open}
				>
					<span className="flex-1 min-w-0 flex items-center gap-1">
						{!single && selectionLength > showCountAt && (
							<span className="px-1.5 mr-1 bg-gray-800/10 dark:bg-gray-700/10 rounded-md text-gray-500 dark:text-gray-400 text-xs shrink-0">
								{selectionLength}
							</span>
						)}
						<span className="flex-1 min-w-0 truncate">
							{selectionLength > 0 ? (
								// Use renderSelection for custom visual content if provided
								renderSelection ? (
									(() => {
										const rendered = renderSelection(normalizedValue, options);
										// Ensure renderSelection returns a renderable value
										if (rendered === null || rendered === undefined) return '';
										if (typeof rendered === 'string' || typeof rendered === 'number')
											return rendered;
										if (React.isValidElement(rendered)) return rendered;
										// If rendered is an object or other non-renderable value, convert to string
										return String(rendered);
									})()
								) : (
									getSelectionText(normalizedValue, options)
								)
							) : (
								<span className="text-gray-500 dark:text-gray-400">
									{placeholder || intl.formatMessage({id: 'Choose...'})}
								</span>
							)}
						</span>
					</span>

					{!disabled &&
						clearable &&
						((single &&
							normalizedValue !== null &&
							normalizedValue !== undefined &&
							normalizedValue !== '') ||
							(!single &&
								Array.isArray(normalizedValue) &&
								normalizedValue.length > 0)) && (
							<div
								className={twMerge(
									'group shrink-0 size-5 flex items-center justify-center hover:bg-gray-800/10 dark:hover:bg-gray-700/10 rounded-full',
									resetButtonClassName,
								)}
							>
								<X
									className="size-3 text-gray-400 dark:text-gray-500 cursor-pointer group-hover:text-gray-600 dark:group-hover:text-gray-300"
									aria-label={intl.formatMessage({id: 'Clear selection'})}
									strokeWidth={2.5}
									onClick={e => handleReset(field, e)}
								/>
							</div>
						)}

					{!customTrigger && (
						<ChevronDown
							className={twMerge(
								'shrink-0 text-gray-400 dark:text-gray-500 pointer-events-none',
								size === 'xs' ? 'h-3 w-3' : 'h-4 w-4',
							)}
						/>
					)}
				</Button>
			);

			return (
				<div className={twMerge('relative', sizeClass, wrapperClassName)}>
					<div ref={dropdownRef} className="h-full w-full flex items-center">
						{triggerElement}
					</div>
					{open && createPortal(menu, document.getElementById('popper'))}
				</div>
			);
		}

		// Expose focus method via imperative handle for direct ref usage (when not using Controller)
		useImperativeHandle(
			ref,
			() => ({
				focus: () => {
					if (referenceElement && referenceElement.focus) {
						referenceElement.focus();
					}
				},
			}),
			[referenceElement],
		);

		if (control) {
			return (
				<Controller
					name={name}
					control={control}
					rules={rules}
					render={({field}) => renderDropdown({...field, disabled, id: id || name})}
				/>
			);
		}

		// Uncontrolled fallback
		return renderDropdown({
			value: normalizeValue(value),
			onChange,
			name,
			id: id || name,
			ref,
		});
	},
);

MultiSelect.displayName = 'MultiSelect';

// Export a convenience component for single selection
const SingleSelect = React.forwardRef((props, ref) => (
	<MultiSelect
		{...props}
		single={true}
		hoistSelected={props.hoistSelected ?? false}
		ref={ref}
	/>
));

SingleSelect.displayName = 'SingleSelect';

export {SingleSelect};
export default MultiSelect;
